import { t } from 'i18next';
import { AppStore } from '../store/AppStore';
import { appSettings } from '../settings';
import {
  ERuleType,
  EFeatureStatus,
  ERuleStrategy,
  ERuleSaveStatus,
  Master,
  EMessageType,
  ECommonDialogType,
} from '../data/Constants';
import {
  getFeatureMARulesForPayload,
  isEqualAssociations,
  getFeatureEffectivityDataForPayload,
  flipCountries,
} from './DataHelperFunctions';
import {
  createRules,
  getMarketAssociations,
  setBatchSaveStatus,
  setStatusOfAllRuleUpdates,
} from '../store/states/ProcosSlice';
import { getToastClassName, initiateToast, updateToast } from './ToastHelper';
import { openDialog } from '../store/states/DialogSlice';
import { Trans } from 'react-i18next';
import WarningIcon from '@mui/icons-material/Warning';
import { createElement, Fragment } from 'react';

import type {
  IEffectivityRequest,
  IRule,
  ISaveRuleToastProps,
  ISaveRuleRequest,
  IFeature,
  IMarketFailedCount,
  IBatchResponseProps,
  IHandleBatchRequestProps,
} from '../../types';
import type {
  IBatchSaveStatus,
  IProcosProduct,
  IProcosState,
  IWorkItemOption,
} from '../../types/IComponentTypes';

// Batch settings from app config
const { BatchSize = 6, QueueSize = 1, RuleStrategy } = appSettings.ProcosSaveBatching || {};

/**
 * Get Request object for Rules
 * @param {IWorkItemOption} workItem information state
 * @param {IProcosProduct} product information state
 * @returns {Promise<ISaveRuleRequest>} Save Rule Request Object
 */
export async function getSaveRulesRequest(
  workItem: IWorkItemOption,
  product: IProcosProduct
): Promise<ISaveRuleRequest | null> {
  const procos = AppStore.getState().procos;
  const allMarkets = procos.markets.map((market) => market.code);

  const productRule = getProductRequest(product, allMarkets);
  const featureRules = getFeatureMARulesForPayload(
    procos.features,
    procos.features.ma,
    procos.product.ma?.updatedMarkets?.length ?? 0,
    allMarkets
  );

  const rules: IRule[] = [...(productRule ? [productRule] : []), ...featureRules];

  if (rules.length === 0) return null;

  return {
    productCode: product.id,
    workItemId: workItem.id,
    ruleType: ERuleType.Market,
    marketRules: rules,
  };
}

/**
 *
 * @param {IWorkItemOption} workItem information state
 * @param {IProcosProduct} product information state
 * @param {IProcosState } procos state
 * @returns {Promise<IEffectivityRequest>} Effectivity request object
 */
export async function getSaveEffectivityRequest(
  workItem: IWorkItemOption,
  product: IProcosProduct,
  procos: IProcosState
): Promise<IEffectivityRequest> {
  const features = procos.features;
  const featureEffectivities = getFeatureEffectivityDataForPayload(features, procos);

  return {
    productCode: product.id,
    workItemId: workItem.id.toString(),
    features: featureEffectivities,
    isActive: procos.productStatus === EFeatureStatus.Active,
  };
}

/**
 * return the product rules  for save Market association api
 * @param {IProcosProduct} product information state
 * @param {string[]} allMarkets is array of all countries
 * @returns {IRule} Product rule object
 */
export function getProductRequest(product: IProcosProduct, allMarkets: string[]): IRule | null {
  if (!product.ma) {
    return null;
  }

  const { initialMarkets, updatedMarkets } = product.ma;
  const hasChanged =
    (initialMarkets.length === 0 && updatedMarkets.length > 0) ||
    (initialMarkets.length > 0 && !isEqualAssociations(initialMarkets, updatedMarkets));

  if (hasChanged) {
    const rule: IRule = {
      code: product.id,
      type: '',
      countries:
        RuleStrategy === ERuleStrategy.Exclusive
          ? flipCountries(allMarkets, updatedMarkets)
          : updatedMarkets,
    };
    if (RuleStrategy === ERuleStrategy.Exclusive) {
      rule.includeMarkets = false;
    }
    return rule;
  }
  return null;
}

/**
 * Handles batching and processing of Create Rules API requests.
 */
export async function handleBatchingForCreateRuleAPI(
  originalPayload: ISaveRuleRequest
): Promise<void> {
  AppStore.dispatch(setStatusOfAllRuleUpdates());
  const payloadData: ISaveRuleRequest[] = [];
  const { productCode, ruleType, workItemId, marketRules } = originalPayload;

  for (let i = 0; i < marketRules.length; i += BatchSize) {
    const batchMarketRules = marketRules.slice(i, i + BatchSize);
    payloadData.push({
      productCode,
      workItemId,
      marketRules: batchMarketRules,
      ruleType: ruleType ?? ERuleType.Market,
    });
  }
  await processPayloadInBatches(payloadData);
}

/**
 * Function to send a single batch of data to the Create Rules API
 * @param { ISaveRuleRequest } batch is a single set of payload to send to API
 * @returns {Promise<void>}
 */
async function processPayloadInBatches(payloadData: ISaveRuleRequest[]): Promise<void> {
  const { product, activeWorkItem } = AppStore.getState().procos;

  payloadData.forEach((_, idx) => {
    const batchSaveItem = { batchNo: idx + 1, batchStatus: ERuleSaveStatus.Inprogress };
    AppStore.dispatch(setBatchSaveStatus(batchSaveItem));
  });

  const totalBatchCount = Math.ceil(payloadData.length / QueueSize);
  let completedBatchCount = 0;
  const initialMessage = t('messages.saveProcos.batchStatus', {
    completedBatchesCount: 0,
    totalBatchCount,
  });
  const toastId = initiateToast(initialMessage);

  let hasErrors = false;
  let response: IBatchResponseProps = { type: '', errorId: '' };
  for (let i = 0; i < payloadData.length; i += QueueSize) {
    try {
      response = await handleBatchRequest({
        batch: payloadData[i],
        toastId,
        completedBatchCount,
        totalBatchCount,
      });

      if (response.type === EMessageType.Error && !response.errorId) {
        hasErrors = true;
      } else if (response.type !== EMessageType.Success) {
        hasErrors = true;
        completedBatchCount = totalBatchCount;
        break;
      }
    } catch {
      hasErrors = true;
    }
    completedBatchCount++;
  }

  //Get the type after all batches completed to show the final message
  let type;
  if (hasErrors) {
    type = EMessageType.Error;
  } else {
    type = EMessageType.Success;
  }

  if (!response?.errorId) {
    //Refresh market associations
    //Restrict getMarketRules API if batch call failed for network issues
    const marketRuleRequest = getMarketRuleRequest(activeWorkItem, product.id);
    AppStore.dispatch(getMarketAssociations(marketRuleRequest));
  }

  //Show toast message to the user after all batches are completed
  updateSaveRuleToast({ toastId, type, completedBatchCount, totalBatchCount });

  // show save dialog message
  showSaveDialog(type, response?.errorId);
}

/**
 * This function dispatch the create rule action for each batch
 * @param {ISaveRuleToastProps} props input props
 * @returns {Promise<boolean| EMessageType>} return message type
 * @returns {IBatchResponseProps} return type and error id
 */
async function handleBatchRequest(props: IHandleBatchRequestProps): Promise<IBatchResponseProps> {
  const { batch, toastId, completedBatchCount, totalBatchCount } = props;

  const currentBatchCount = completedBatchCount + 1;
  const isAllBatchesProcessed = currentBatchCount === totalBatchCount;

  const response = await AppStore.dispatch(
    createRules({ request: batch, isAllBatchesProcessed })
  ).unwrap();

  const { isSuccess, errorId } = response?.data || {};
  const type = isSuccess ? EMessageType.Success : EMessageType.Error;
  updateSaveRuleToast({
    toastId,
    type,
    completedBatchCount: currentBatchCount,
    totalBatchCount,
    errorId,
  });
  return { type, errorId };
}

/**
 * This function dispatch the action to show the save dialog
 * @param {string } type message type
 * @param {string } errorId api error code
 */
function showSaveDialog(messageType: string, errorId?: string) {
  const dialogType = getAllBatchSaveStatus();

  let message;

  if (errorId) {
    message = t('messages.error.' + errorId);
  } else {
    message =
      messageType === EMessageType.Success
        ? t('messages.submit.RulesSaved')
        : getBatchFailedMessage();
  }

  AppStore.dispatch(
    openDialog({
      type: dialogType,
      title: t('dialogBox.save'),
      data: message,
    })
  );
}

/**
 * This function show toast message for save procos for each batches
 * @param {ISaveRuleToastProps} props input props
 */
function updateSaveRuleToast(props: ISaveRuleToastProps): void {
  const { toastId, type, completedBatchCount, totalBatchCount } = props;

  //get toast message type based on diffrent api error message

  const className = getToastClassName(type);
  const finalMessage =
    type === EMessageType.Success
      ? t('messages.saveProcos.batchSuccess')
      : t('messages.saveProcos.batchFailed');

  const isBatchCompleted = completedBatchCount === totalBatchCount;

  updateToast({
    toastId,
    type,
    className,
    autoClose: isBatchCompleted,
    message: `${t('messages.saveProcos.batchStatus', {
      completedBatchesCount: completedBatchCount,
      totalBatchCount,
    })}${isBatchCompleted ? finalMessage : ''}`,
  });
}

/**
 * Returns the type of dialog based on batch save status.
 * @returns {ECommonDialogType}
 */
function getAllBatchSaveStatus(): ECommonDialogType {
  const { batchSaveStatus } = AppStore.getState().procos;
  const allRulesSavedSuccessfully = batchSaveStatus?.every(
    (item: IBatchSaveStatus) => item.batchStatus !== 'F'
  );
  return allRulesSavedSuccessfully ? ECommonDialogType.Success : ECommonDialogType.Error;
}

/**
 * This function returns the save rule failed message
 * @returns {ReactElement} return failed message
 */
function getBatchFailedMessage(): React.ReactElement {
  const { features } = AppStore.getState().procos;
  const { data: featureData, recordsPerPage } = features;

  const failedRulesByPage = new Map<number, IMarketFailedCount>();
  const initFailedCount: IMarketFailedCount = {
    properties: 0,
    rules: 0,
  };

  featureData?.forEach((feature: IFeature, index: number) => {
    const { saveStatus, failed } = feature;

    if (saveStatus === ERuleSaveStatus.Failed || saveStatus === ERuleSaveStatus.Partial) {
      const pageNumber = Math.ceil((index + 1) / recordsPerPage);
      const currentFailedCount: IMarketFailedCount =
        failedRulesByPage.get(pageNumber) ?? initFailedCount;

      if (failed?.isPropertyFailed) {
        currentFailedCount.properties += 1;
      }
      if (failed?.isRuleFailed) {
        currentFailedCount.rules += 1;
      }

      failedRulesByPage.set(pageNumber, currentFailedCount);
    }
  });

  const listItems = Array.from(failedRulesByPage.entries()).map(([pageNo, currentFailedCount]) =>
    createElement(
      'li',
      { key: pageNo },

      createElement(Trans, {
        i18nKey: 'messages.saveProcos.rulesFailed',
        values: {
          rule: currentFailedCount.rules,
          property: currentFailedCount.properties,
          pageNo,
        },
        components: {
          ruleIcon:
            currentFailedCount.rules > 0
              ? createElement(WarningIcon, {
                  sx: { fontSize: '1.1rem', color: 'orange', verticalAlign: 'middle' },
                })
              : createElement(Fragment),
          propertyIcon:
            currentFailedCount.properties > 0
              ? createElement(WarningIcon, {
                  sx: { fontSize: '1.1rem', color: 'orange', verticalAlign: 'middle' },
                })
              : createElement(Fragment),
        },
      })
    )
  );

  return createElement('div', null, t('messages.saveProcos.rulesFailedTopic'), listItems);
}

/**
 * Builds the request payload for getMarketAssociation Rule API.
 */
export function getMarketRuleRequest(workItem: IWorkItemOption, productId: string) {
  return {
    productCode: productId,
    onlyMarketRules: true,
    workItemId: workItem && workItem.name !== Master.name ? workItem.id.toString() : '',
  };
}
